import { Fragment } from "react";
import {
  useSortable,
  SortableContext,
  horizontalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Row, Table } from "@tanstack/react-table";
import { Reorder } from "@certa/icons/components/Reorder";

import { InternalTableCell } from "../InternalTableCell/InternalTableCell";
import { InternalTableRowExpanded } from "./InternalTableRowExpanded";

import tableStyles from "../Table.module.css";
import styles from "./InternalTableRow.module.css";

type InternalTableRowDraggableProps<TableData> = {
  row: Row<TableData>;
  columnOrder: string[];
  expandableRowId: string;
  onRowSelect: (rowId: string) => void;
  onRowExpand: (rowId: string) => void;
  isReorderingEnabled: boolean;
  canDrag?: boolean;
  tableInstance: Table<TableData>;
  rowClassName?: string;
  canShowSelectionColumn?: boolean;
};

export const InternalTableRowDraggable = <
  TableData extends Record<string, any>
>(
  props: InternalTableRowDraggableProps<TableData>
) => {
  const {
    row,
    columnOrder,
    expandableRowId,
    onRowSelect,
    onRowExpand,
    isReorderingEnabled,
    canDrag = true,
    tableInstance,
    rowClassName,
    canShowSelectionColumn
  } = props;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: row.id,
    disabled: !isReorderingEnabled || !canDrag
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const combinedClassName =
    `${tableStyles.tableRow} ${rowClassName || ""}`.trim();

  return (
    <Fragment>
      <tr
        ref={setNodeRef}
        style={style}
        className={combinedClassName}
        data-dragging={isDragging}
        data-table-row-id={row.id}
      >
        {isReorderingEnabled && (
          <td className={styles.reorderCell} {...attributes} {...listeners}>
            <Reorder size={16} />
          </td>
        )}
        <SortableContext
          items={columnOrder}
          strategy={horizontalListSortingStrategy}
        >
          {row.getVisibleCells().map((cell, index) => (
            <InternalTableCell
              key={`${cell.id}-${index}`}
              cell={cell}
              onRowSelect={onRowSelect}
              onRowExpand={onRowExpand}
              expandableRowId={expandableRowId}
              canShowSelectionColumn={canShowSelectionColumn}
            />
          ))}
        </SortableContext>
      </tr>
      <InternalTableRowExpanded
        row={row}
        tableInstance={tableInstance}
        expandableRowId={expandableRowId}
      />
    </Fragment>
  );
};
